export const fourDhyanas = {
  id: "four-dhyanas",
  title: "The Four Dhyānas",
  subtitle: "Catvāri Dhyānāni — The Four Meditative Absorptions",
  description:
    "The four dhyānas are progressively refined states of meditative absorption in the form realm (rūpadhātu). Each dhyāna represents a deeper level of concentration and mental refinement. They form the basis for developing the supernormal powers (abhijñā) and are mastered by the bodhisattva on the third bhūmi.",
  crossReferences: [
    "four-formless-absorptions",
    "nine-stages-of-shamatha",
    "ten-bhumis",
    "five-hindrances",
  ],
  items: [
    {
      number: 1,
      sanskrit: "Prathamadhyāna",
      name: "First Dhyāna",
      description:
        "Achieved by overcoming the five hindrances. Characterized by applied thought (vitarka), sustained thought (vicāra), joy (prīti), bliss (sukha), and one-pointedness (ekāgratā). The meditator is withdrawn from sensory desire and unwholesome states.",
      factors: ["Vitarka (Applied Thought)", "Vicāra (Sustained Thought)", "Prīti (Joy)", "Sukha (Bliss)", "Ekāgratā (One-pointedness)"],
      abandoned: "Five hindrances (sensory desire, ill-will, sloth-torpor, restlessness-worry, doubt)",
    },
    {
      number: 2,
      sanskrit: "Dvitīyadhyāna",
      name: "Second Dhyāna",
      description:
        "Applied and sustained thought cease, leaving the mind in serene clarity. Characterized by inner confidence (adhyātma-samprasāda), joy, bliss, and one-pointedness. The mind settles into an unwavering composure born of concentration.",
      factors: ["Adhyātma-samprasāda (Inner Confidence)", "Prīti (Joy)", "Sukha (Bliss)", "Ekāgratā (One-pointedness)"],
      abandoned: "Vitarka (applied thought) and vicāra (sustained thought)",
    },
    {
      number: 3,
      sanskrit: "Tṛtīyadhyāna",
      name: "Third Dhyāna",
      description:
        "Joy fades, leaving equanimity (upekṣā), mindfulness, and clear comprehension. The meditator abides in bliss that the noble ones describe as 'abiding in equanimity, mindful, and clearly comprehending.' This is said to be the most pleasant abiding in all of saṃsāra.",
      factors: ["Upekṣā (Equanimity)", "Smṛti (Mindfulness)", "Samprajanya (Clear Comprehension)", "Sukha (Bliss)", "Ekāgratā (One-pointedness)"],
      abandoned: "Prīti (joy/rapture)",
    },
    {
      number: 4,
      sanskrit: "Caturthadhyāna",
      name: "Fourth Dhyāna",
      description:
        "Both bliss and suffering cease completely, leaving pure equanimity and mindfulness. The breath becomes extremely subtle or stops entirely. This is the ideal base for developing insight (vipaśyanā) and the supernormal powers. From here, one can enter the formless absorptions.",
      factors: ["Upekṣā (Equanimity)", "Smṛti (Mindfulness)", "Ekāgratā (One-pointedness)"],
      abandoned: "Sukha (bliss) and duḥkha (suffering) — the mind rests in perfect neutrality",
    },
  ],
};

export const fourFormlessAbsorptions = {
  id: "four-formless-absorptions",
  title: "The Four Formless Absorptions",
  subtitle: "Catvāri Ārūpya-samāpatti — The Four Formless Attainments",
  description:
    "The four formless absorptions transcend the form realm entirely, representing the highest states of concentration within saṃsāra. Each successively refines the object of meditation until even the subtlest conceptual reference point is transcended. While not liberating in themselves, they demonstrate the mind's capacity for profound refinement and serve as a basis for developing insight.",
  crossReferences: [
    "four-dhyanas",
    "three-realms",
    "ten-bhumis",
    "nine-stages-of-shamatha",
  ],
  items: [
    {
      number: 1,
      sanskrit: "Ākāśānantyāyatana",
      name: "Infinite Space",
      description:
        "Transcending all perception of form, the meditator dissolves the sense of physical boundaries and rests in the perception of boundless, infinite space. All material form is completely transcended — the mind expands into limitless spatial awareness.",
      transcended: "All perception of material form (rūpa-saṃjñā)",
      object: "Boundless space — infinite, open, without center or periphery",
    },
    {
      number: 2,
      sanskrit: "Vijñānānantyāyatana",
      name: "Infinite Consciousness",
      description:
        "Transcending the perception of space, the meditator turns attention to consciousness itself, recognizing it as boundless and infinite. The focus shifts from the container (space) to the awareness that perceives it.",
      transcended: "Perception of infinite space",
      object: "Boundless consciousness — awareness aware of its own infinity",
    },
    {
      number: 3,
      sanskrit: "Ākiṃcanyāyatana",
      name: "Nothingness",
      description:
        "Transcending even infinite consciousness, the meditator rests in the perception that 'there is nothing at all.' This is not nihilistic negation but a profound letting go of all objects, including consciousness as an object. The mind rests in utter simplicity.",
      transcended: "Perception of infinite consciousness",
      object: "The absence of any object — 'there is nothing whatsoever'",
    },
    {
      number: 4,
      sanskrit: "Naiva-saṃjñā-nāsaṃjñāyatana",
      name: "Neither Perception Nor Non-Perception",
      description:
        "The most refined state of saṃsāric consciousness. Perception becomes so subtle that it can neither be called perception nor non-perception. This is the peak of mundane concentration — extremely peaceful but still within saṃsāra. The Buddha's teachers Āḷāra Kālāma and Uddaka Rāmaputta respectively taught the third and fourth formless absorptions as the highest goal.",
      transcended: "Perception of nothingness",
      object: "A state so refined that perception and non-perception are indistinguishable",
    },
  ],
};

export const fourImmeasurables = {
  id: "four-immeasurables",
  title: "The Four Immeasurables",
  subtitle: "Catvāri Apramāṇāni — The Four Boundless Qualities",
  description:
    "The four immeasurables (also called the four brahmavihāras or 'divine abidings') are qualities of heart that, when fully developed, extend without limit to all sentient beings without exception. They are both meditative practices and qualities to be cultivated in daily life. In Mahāyāna Buddhism, they are inseparable from bodhicitta and form the emotional foundation of the bodhisattva path.",
  crossReferences: [
    "bodhicitta",
    "ten-bhumis",
    "six-paramitas",
    "compassion",
  ],
  items: [
    {
      number: 1,
      sanskrit: "Maitrī",
      pali: "Mettā",
      name: "Loving-Kindness",
      description:
        "The wish for all beings to have happiness and the causes of happiness. Maitrī is the antidote to ill-will and hatred. It begins with those close to us and gradually extends to all beings without exception — friends, strangers, and enemies alike.",
      nearEnemy: "Attachment (selfish love that expects something in return)",
      farEnemy: "Ill-will and hatred",
      aspiration: "May all beings have happiness and the causes of happiness",
      practice:
        "Begin with self, extend to loved ones, neutral persons, difficult persons, and finally all beings throughout space.",
    },
    {
      number: 2,
      sanskrit: "Karuṇā",
      name: "Compassion",
      description:
        "The wish for all beings to be free from suffering and the causes of suffering. Karuṇā is the heart of the bodhisattva path — the great compassion (mahākaruṇā) that does not rest until all beings are liberated. When united with wisdom, it becomes the unstoppable force that drives the bodhisattva through all ten bhūmis.",
      nearEnemy: "Pity (which maintains a sense of superiority and separateness)",
      farEnemy: "Cruelty and indifference to suffering",
      aspiration: "May all beings be free from suffering and the causes of suffering",
      practice:
        "Contemplate the suffering of beings in each realm; develop the heartfelt wish for their liberation.",
    },
    {
      number: 3,
      sanskrit: "Muditā",
      name: "Sympathetic Joy",
      description:
        "The ability to rejoice in the happiness, success, and good qualities of others. Muditā is the antidote to jealousy and envy. It is the most joyful of the immeasurables — when perfected, the bodhisattva has infinite sources of joy because they rejoice in the happiness of all beings.",
      nearEnemy: "Exhilaration (frivolous excitement based on worldly gain)",
      farEnemy: "Jealousy and envy",
      aspiration: "May all beings never be separated from the great happiness devoid of suffering",
      practice:
        "Actively rejoice in others' achievements, good fortune, and spiritual progress.",
    },
    {
      number: 4,
      sanskrit: "Upekṣā",
      name: "Equanimity",
      description:
        "An even-minded, balanced state free from attachment and aversion toward any being. Upekṣā is not indifference but an impartial openness to all beings equally. It is the quality that ensures the other three immeasurables are truly boundless — extending to all beings without bias or partiality.",
      nearEnemy: "Indifference (apathetic disengagement masquerading as equanimity)",
      farEnemy: "Attachment and aversion (bias, partiality)",
      aspiration: "May all beings abide in equanimity, free from attachment to close ones and aversion to others",
      practice:
        "Recognize the equal wish of all beings for happiness; release bias toward friend, stranger, and enemy.",
    },
  ],
};

export const nineShamatha = {
  id: "nine-stages-of-shamatha",
  title: "The Nine Stages of Śamatha",
  subtitle: "Nine Mental Abidings — Stages of Calm Abiding Meditation",
  description:
    "The nine stages of śamatha (calm abiding) describe the progressive development of single-pointed concentration. They map the journey from a completely distracted mind to the attainment of perfect mental quiescence (śamatha). Five faults are overcome by applying eight antidotes across these nine stages.",
  crossReferences: ["four-dhyanas", "five-hindrances", "six-paramitas"],
  items: [
    { number: 1, name: "Placing the Mind", sanskrit: "Cittasthāpana", description: "First directing the mind to the meditation object. Attention is placed but immediately wanders. The meditator discovers just how wild and uncontrolled the mind is.", power: "Hearing/Study (śruta)" },
    { number: 2, name: "Continuous Placement", sanskrit: "Saṃsthāpana", description: "Able to maintain attention for brief periods before distraction. There are short moments of focus amid much distraction.", power: "Reflection (cintā)" },
    { number: 3, name: "Patch-like Placement", sanskrit: "Avasthāpana", description: "Distraction is recognized quickly and attention is restored. Focus is like patches sewn together — periods of concentration interrupted by wandering.", power: "Mindfulness (smṛti)" },
    { number: 4, name: "Close Placement", sanskrit: "Upasthāpana", description: "The mind no longer completely loses the object. Gross distraction is overcome, but subtle dullness and excitation remain.", power: "Mindfulness (smṛti)" },
    { number: 5, name: "Taming", sanskrit: "Damana", description: "The meditator takes joy in concentration. Subtle dullness is overcome through heightening the mind's clarity and intensity.", power: "Introspective awareness (samprajanya)" },
    { number: 6, name: "Pacifying", sanskrit: "Śamana", description: "Any resistance or aversion to meditation is pacified. Subtle excitation is overcome. The mind settles more deeply.", power: "Introspective awareness (samprajanya)" },
    { number: 7, name: "Fully Pacifying", sanskrit: "Vyupaśamana", description: "Even the subtlest dullness and excitation can be removed. The meditator is able to sustain long sessions of clear, stable focus.", power: "Diligence (vīrya)" },
    { number: 8, name: "Single-Pointed", sanskrit: "Ekotīkaraṇa", description: "Concentration is sustained effortlessly for the duration of the session. Only a slight initial effort is needed to engage with the object.", power: "Diligence (vīrya)" },
    { number: 9, name: "Balanced Placement", sanskrit: "Samādhāna", description: "Concentration is completely effortless and spontaneous. The mind rests in perfect equipoise. This leads to the attainment of mental pliancy (praśrabdhi) and the full accomplishment of śamatha.", power: "Familiarity (abhyāsa)" },
  ],
};
